import React, {
  cloneElement,
  forwardRef,
  useCallback,
  useEffect,
  useState,
  useMemo
} from "react"
import {
  Box,
  Divider,
  ListSubheader,
  Menu,
  PopoverOrigin,
  PopoverPosition,
  PopoverReference,
  Typography
} from "@mui/material"
import { RuleName, useStyles } from "./styles"
import { transformOptionsToGroups } from "./helpers"
import {
  DropdownMenuItem,
  DropdownMenuOptions,
  OptionHandler
} from "./DropdownMenuItem"

interface Props {
  id: string
  open?: boolean
  anchor: React.ReactElement
  getAnchorId?: () => HTMLElement
  options?: DropdownMenuOptions[]
  onSelect?: (option: DropdownMenuOptions) => void
  disablePortal?: boolean
  anchorReference?: PopoverReference
  anchorPosition?: PopoverPosition
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  withCheckbox?: boolean
  dropdownTitle?: string
  className?: string
  wrapperClassName?: string
  MenuListProps?: Record<string, any>
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  onClose?: () => void
  classes?: Partial<Record<RuleName, string>>
  groupBy?: (option: DropdownMenuOptions) => string
  "aria-label"?: string
}

const DropdownMenu = forwardRef<any, React.PropsWithChildren<Props>>(
  (props, ref) => {
    const {
      id,
      open: outerOpen,
      anchor,
      getAnchorId,
      options = [],
      onSelect,
      className,
      wrapperClassName,
      anchorPosition,
      disablePortal = false,
      anchorReference = "anchorEl",
      anchorOrigin = {
        vertical: "bottom",
        horizontal: "right"
      },
      transformOrigin = {
        vertical: "top",
        horizontal: "right"
      },
      withCheckbox = false,
      dropdownTitle,
      MenuListProps = {},
      onClick,
      onClose,
      children,
      classes: outerClasses,
      groupBy,
      "aria-label": ariaLabelProp
    } = props
    const ariaLabel = ariaLabelProp === "" ? null : ariaLabelProp
    const { classes, cx } = useStyles()

    const [anchorId, setAnchorId] = useState<null | HTMLElement>(null)
    const [open, setOpen] = useState<boolean>(outerOpen ?? false)

    // Calculate buttonId so it can be used in handleClose
    const buttonId = useMemo(
      () => (anchor?.props?.id ? anchor.props.id : `${id}-button`),
      [anchor, id]
    )

    useEffect(() => {
      setOpen(outerOpen ?? false)
    }, [outerOpen])

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setAnchorId(getAnchorId?.() ?? event.currentTarget)
        setOpen(true)
        onClick?.(event)
        event?.stopPropagation()
      },
      [onClick, getAnchorId]
    )

    const handleClose = useCallback(
      (event?: React.MouseEvent<HTMLElement>) => {
        setAnchorId(null)
        setOpen(false)
        onClose?.()
        event?.stopPropagation()
      },
      [onClose]
    )

    const handleAction = useCallback(
      (action: OptionHandler) => (event: React.MouseEvent<HTMLElement>) => {
        action?.(event)
        !withCheckbox && handleClose()
      },
      [handleClose]
    )

    const handleKeyDown = (event: React.MouseEvent<HTMLElement>) => {
      event?.stopPropagation()
    }

    const anchorElement =
      anchor &&
      cloneElement(anchor, {
        id: buttonId,
        "aria-controls": open ? `${id}-menu` : undefined,
        "aria-haspopup": "menu",
        "aria-expanded": open ? "true" : "false",
        "aria-label": ariaLabel ?? "Open menu",
        onClick: handleClick,
        onKeyDown: handleKeyDown
      })

    const transformedOptions: Record<string, DropdownMenuOptions[]> = useMemo(
      () =>
        !!groupBy
          ? transformOptionsToGroups(options, groupBy)
          : { "": options },
      [options, groupBy]
    )

    return (
      <Box className={wrapperClassName} ref={ref}>
        {anchorElement}
        <Menu
          disablePortal={disablePortal}
          id={`${id}-menu`}
          className={cx(classes.menu, outerClasses?.menu, className)}
          open={open}
          anchorEl={anchorId}
          onClose={handleClose}
          anchorPosition={anchorPosition}
          anchorReference={anchorReference}
          anchorOrigin={anchorOrigin as any}
          transformOrigin={transformOrigin as any}
          MenuListProps={{
            "aria-labelledby": buttonId,
            role: "menu",
            ...MenuListProps
          }}
          classes={{
            list: cx(classes.menuList, outerClasses?.menuList),
            paper: cx(classes.paper, outerClasses?.paper)
          }}
        >
          {dropdownTitle && (
            <Typography
              className={cx(classes.dropdownTitle, outerClasses?.dropdownTitle)}
              variant="subtitle1"
            >
              {dropdownTitle}
            </Typography>
          )}
          {Object.entries(transformedOptions)?.map(
            ([groupTitle, options], groupIndex, groupArray) => {
              const elements = []

              if (!!groupBy && !!groupTitle) {
                elements.push(
                  <Typography key={groupTitle} className={classes.groupTitle}>
                    {groupTitle}
                  </Typography>
                )
              }

              elements.push(
                ...options?.map((option, i) => {
                  const {
                    label,
                    id: itemId,
                    selected,
                    icon,
                    action,
                    href,
                    divider = false,
                    header = false,
                    disabled = false,
                    role,
                    "data-automation-id": dataAutomationId
                  } = option

                  if (divider) {
                    return <Divider key={itemId || i} />
                  }

                  if (header) {
                    return (
                      <ListSubheader
                        key={itemId ?? i}
                        component="div"
                        id="products-list"
                        className={classes.menuListItemSubheader}
                        color="inherit"
                        style={{ position: "unset" }}
                      >
                        {label}
                      </ListSubheader>
                    )
                  }

                  const handler = handleAction(
                    action ?? (onSelect ? () => onSelect(option) : undefined)
                  )

                  return (
                    <DropdownMenuItem
                      key={itemId || i}
                      classes={outerClasses}
                      withCheckbox={withCheckbox}
                      disabled={disabled}
                      selected={selected}
                      role={role}
                      icon={icon}
                      label={label}
                      data-automation-id={dataAutomationId}
                      action={handler}
                      href={href}
                    />
                  )
                })
              )

              if (groupIndex !== groupArray.length - 1) {
                elements.push(
                  <Divider key={groupIndex} className={classes.groupDivider} />
                )
              }

              return elements
            }
          )}
          {children}
        </Menu>
      </Box>
    )
  }
)

export default DropdownMenu
